import { z } from 'zod'

/**
 * A point in time, canonically formatted: `YYYY-MM-DDTHH:MM:SS.sssZ`.
 *
 * ## Why the format is pinned rather than merely documented
 *
 * The bi-temporal rule lookup compares instants to decide which rule versions
 * the system could have known about at a past moment. That comparison is a string
 * comparison, and a string comparison of ISO 8601 is only sound when every value
 * shares one shape. Both of these are valid ISO 8601 and both order WRONGLY:
 *
 *   "2026-03-01T01:00:00Z" > "2026-02-28T20:00:00-08:00"   // but is EARLIER
 *   "2026-03-01T00:00:00.500Z" < "2026-03-01T00:00:00Z"    // but is LATER
 *
 * An offset or a missing millisecond field silently inverts the ordering, and a
 * rule lookup that picks the wrong version produces money that is plausible and
 * wrong. So this type accepts exactly one shape — UTC, millisecond precision —
 * and refuses everything else at the boundary rather than trusting a convention.
 */
declare const instantBrand: unique symbol

const canonicalInstant = z
  .string()
  .regex(
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/,
    'must be a canonical UTC instant, YYYY-MM-DDTHH:MM:SS.sssZ',
  )
  .refine((value) => {
    // A regex accepts 2026-02-31T25:00:00.000Z. Round-tripping through Date
    // proves the components are real: an invalid date yields NaN, and a rolled
    // over one yields a different canonical string than it started with.
    const parsed = new Date(value)
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString() === value
  }, 'must be a real instant')

export const Instant = canonicalInstant.brand<typeof instantBrand>()
export type Instant = z.infer<typeof Instant>

/** Parse-or-throw, for the edges where a raw string arrives. */
export const instant = (value: string): Instant => Instant.parse(value)

/**
 * Canonicalise any `Date` into an `Instant`.
 *
 * The one blessed way to produce an instant from a clock reading, so a caller
 * never hand-formats one and reintroduces the offset hazard above.
 */
export const instantFrom = (date: Date): Instant => instant(date.toISOString())

/**
 * ★★★ S-075 — THE EXCLUSIVE UPPER BOUND THAT MAKES AN INCLUSIVE MILLISECOND
 * BOUND ACTUALLY INCLUSIVE.
 *
 * An `Instant` names a MILLISECOND. Every timestamp column in this product is
 * `timestamptz`, which PostgreSQL stores at **microsecond** precision, so a
 * stored instant is almost never equal to the millisecond that names it —
 * measured on a real population, **29 of 29 rows** carried non-zero digits
 * below the millisecond. That makes the obvious upper bound wrong:
 *
 *     occurred_at <= '2026-08-21T02:03:22.520Z'   -- EXCLUDES …520240
 *
 * so a caller who reads a row, echoes its own reported instant back as an
 * inclusive upper bound, and asks for *"everything up to and including this
 * one"* is answered **without it**. That was S-075's first consequence and it
 * reached four routes at once.
 *
 * This returns the instant **one millisecond later**, to be compared with a
 * STRICT `<`:
 *
 *     occurred_at < '2026-08-21T02:03:22.521Z'    -- includes every µs of …520
 *
 * which admits the whole of the millisecond the caller named and nothing after
 * it, whatever precision the column actually keeps.
 *
 * ## ⚠ Why not simply widen `Instant` to microseconds
 *
 * Rejected, and this is the alternative a reader will reach for. `Instant`'s
 * three-fractional-digit shape is load-bearing for the bi-temporal rule lookup
 * documented above — every clause-bound instant in the product is compared as a
 * string, and a value with a different number of fractional digits orders
 * wrongly against one without. Widening the type to fix a filter bound would
 * reach the money. The bound is the thing that was wrong, so the bound is what
 * changes.
 *
 * ## ⚠ What this is NOT for
 *
 * **A bound generated by the system from the same clock as the column** — a
 * paging anchor read from `now()` — must keep its exact `<=`. Widening such an
 * anchor by a millisecond reopens the window between the read and the next
 * page, which is precisely the silent skip S-075's fix 3 closed. The two cases
 * are different because a caller's bound is a millisecond they can name and an
 * anchor is an instant only the database knows; see `AuditListFilter`'s
 * `occurredTo` vs `occurredAtMost`.
 */
export const millisecondAfter = (value: string): Instant =>
  instantFrom(new Date(new Date(instant(value)).getTime() + 1))

/**
 * ★★★ S-078 — {@link millisecondAfter} for a bound that travels as a `Date`.
 *
 * The bi-temporal `?asRecordedAt=` reads bind a JS `Date` straight into the
 * query rather than a canonical string, so they need the same one-millisecond
 * exclusive bound without a round trip through `Instant`. Same rule, same
 * reason — see {@link millisecondAfter} for both, and
 * `@prs2/persistence`'s `asRecordedAtRewind` for the pair of SQL fragments
 * this feeds.
 *
 * ⚠ A `Date` already holds only milliseconds, so there is nothing to validate
 * and nothing to lose here — but an INVALID date must not silently become
 * `NaN + 1`, which `new Date(NaN)` would carry into a query as `null` and
 * quietly drop the bound. So it is refused.
 */
export const millisecondAfterDate = (date: Date): Date => {
  const t = date.getTime()
  if (Number.isNaN(t)) throw new TypeError('millisecondAfterDate: not a real date, so it cannot be a bound')
  return new Date(t + 1)
}

/**
 * ★★★ WAVE 27 — MOVED HERE FROM `services/transfer`, because a second context
 * now needs it and that file's own comment forbids a second copy.
 *
 * The operating day this product cuts on. It is an **INFERENCE, not a citation**:
 * no clause in the received pack states an operating timezone. PRS 2.0 is
 * operated from Singapore and P2C11 §3.6 requires the data and its processing to
 * stay there — cited as the reasoning, never as authority — and the inference is
 * carried on the assumptions register as **A-20**.
 *
 * ⚠ **Declared exactly once, so a reviewer can disagree with it in one place.**
 * That sentence was already in `inbound-file-repository.ts`, which is where this
 * lived until the dashboard's decision series needed to cut `occurred_at` on the
 * same day boundary. Billing importing it from `@prs2/transfer` would be a
 * cross-context member import, which `packages/architecture/src/boundaries.test.ts`
 * refuses; copying it into billing would break the one-place promise. Moving it
 * into the contract both contexts already depend on keeps both properties.
 * `services/transfer` re-exports it, so every existing import site is unchanged.
 *
 * ⚠ It is passed as a BIND PARAMETER to every `to_char`/`AT TIME ZONE`, never
 * inlined into SQL.
 */
export const PRS2_OPERATING_TIME_ZONE = 'Asia/Singapore'
